import { getDateDifference, pluralize } from '@travelata-libs/utils';
import type { SearchCriteria, Selection, TouristsSelection } from '@/entities/searchCriteria';
import { flexCountWord, formatDdMmYyyy, getAnchor, parseDate } from '@/features/searchForm/dateFrom';
import { formatNightsRange, normalizeRange } from '@/features/searchForm/nightsRange';
import { totalTourists } from '@/features/searchForm/touristsRooms';
import type { DateRange, NightsRange } from '@/shared/model';
import { SERP_ALTERNATIVE_SEARCH_SUMMARY_TEXTS, TOURISTS_PLURAL_FORMS } from '../config/texts';

function formatCountry(destination: Selection | null): string {
  return destination && destination.kind !== 'none' ? destination.country.name : '';
}

function formatDay(date: string): string {
  return formatDdMmYyyy(parseDate(date));
}

function isFlexibleAroundAnchor({ from, to, daysFlexible }: DateRange): boolean {
  if (daysFlexible <= 0) return false;

  return getDateDifference(parseDate(to), parseDate(from), 'days') === daysFlexible * 2;
}

function formatDateRange(dateRange: DateRange): string {
  const { checkInPrefix, checkOutJoin, flexSign } = SERP_ALTERNATIVE_SEARCH_SUMMARY_TEXTS;

  if (isFlexibleAroundAnchor(dateRange)) {
    const anchor = formatDdMmYyyy(getAnchor(dateRange));

    return `${checkInPrefix}${anchor} ${flexSign}${flexCountWord(dateRange.daysFlexible)}`;
  }

  const checkIn = `${checkInPrefix}${formatDay(dateRange.from)}`;
  if (dateRange.from === dateRange.to) return checkIn;

  return `${checkIn}${checkOutJoin}${formatDay(dateRange.to)}`;
}

function formatNights(nights: NightsRange): string {
  const { from, to } = normalizeRange(nights);

  return `${SERP_ALTERNATIVE_SEARCH_SUMMARY_TEXTS.nightsPrefix}${formatNightsRange(from, to)}`;
}

function formatTourists(tourists: TouristsSelection): string {
  const total = totalTourists(tourists);
  const word = pluralize(TOURISTS_PLURAL_FORMS, total, false);

  return `${total}${SERP_ALTERNATIVE_SEARCH_SUMMARY_TEXTS.touristsJoin}${word}`;
}

export function formatAlternativeSearchSummary(criteria: SearchCriteria): string {
  const { destination, dateRange, nights, tourists } = criteria;

  return [
    formatCountry(destination),
    dateRange ? formatDateRange(dateRange) : '',
    nights ? formatNights(nights) : '',
    tourists ? formatTourists(tourists) : '',
  ]
    .filter(Boolean)
    .join(SERP_ALTERNATIVE_SEARCH_SUMMARY_TEXTS.join);
}
